import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { MenuCategory, tableOrderService } from "@/api/services/table-order.service";
import { dialogService } from "@/utils/dialogService";

type MenuCategoryToggleField = "is_active" | "is_web_visible" | "is_pos_visible" | "is_kiosk_visible";

const menuCategoryToggleMeta: Record<
  MenuCategoryToggleField,
  {
    enabledLabel: string;
    disabledLabel: string;
    enabledColor: string;
  }
> = {
  is_active: {
    enabledLabel: "활성",
    disabledLabel: "비활성",
    enabledColor: "soft-success",
  },
  is_web_visible: {
    enabledLabel: "노출",
    disabledLabel: "숨김",
    enabledColor: "soft-primary",
  },
  is_pos_visible: {
    enabledLabel: "노출",
    disabledLabel: "숨김",
    enabledColor: "soft-info",
  },
  is_kiosk_visible: {
    enabledLabel: "노출",
    disabledLabel: "숨김",
    enabledColor: "soft-warning",
  },
};

const MenuCategories = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<MenuCategory[]>([]);
    const [loading, setLoading] = useState(false);
    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const response = await tableOrderService.listAllMenuCategories();
            setItems(response.results || []);
        }
        catch (error) {
            toast.error("카테고리 목록을 불러오지 못했습니다.");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        loadItems();
    }, [loadItems]);
    const handleToggleField = async (item: MenuCategory, field: MenuCategoryToggleField) => {
        try {
        await tableOrderService.updateMenuCategory(item.id, { [field]: !item[field] });
            await loadItems();
            toast.success("상태가 변경되었습니다.");
        }
        catch (error) {
            toast.error("상태 변경에 실패했습니다.");
        }
    };
    const handleDelete = async (item: MenuCategory) => {
        if (!await dialogService.confirm(`'${item.name}' 카테고리를 삭제하시겠습니까?`))
            return;
        try {
            await tableOrderService.deleteMenuCategory(item.id);
            await loadItems();
            toast.success("카테고리가 삭제되었습니다.");
        }
        catch (error) {
            toast.error("카테고리 삭제에 실패했습니다.");
        }
    };
    return (<div className="page-content">
      <Container fluid>
        <BreadCrumb title="메뉴 카테고리" pageTitle="현장주문"/>

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">카테고리 목록</h5>
            <Button color="primary" onClick={() => navigate("/table-order/menu-category-form/0")}>추가</Button>
          </CardHeader>
          <CardBody>
            {loading ? (<p className="text-muted mb-0">불러오는 중...</p>) : (<div className="w-full overflow-x-auto">
                <table className="w-full mb-0 text-sm border-y border-slate-200">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                    <tr>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">ID</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">이름</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">정렬</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">활성</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">웹 노출</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">POS 노출</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">키오스크 노출</th>
                      <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item) => (<tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.id}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.name}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">{item.display_order}</td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button
                            color={item.is_active ? menuCategoryToggleMeta.is_active.enabledColor : "soft-secondary"}
                            size="sm"
                            onClick={() => handleToggleField(item, "is_active")}
                          >
                            {item.is_active ? menuCategoryToggleMeta.is_active.enabledLabel : menuCategoryToggleMeta.is_active.disabledLabel}
                          </Button>
                        </td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button
                            color={item.is_web_visible ? menuCategoryToggleMeta.is_web_visible.enabledColor : "soft-secondary"}
                            size="sm"
                            onClick={() => handleToggleField(item, "is_web_visible")}
                          >
                            {item.is_web_visible ? menuCategoryToggleMeta.is_web_visible.enabledLabel : menuCategoryToggleMeta.is_web_visible.disabledLabel}
                          </Button>
                        </td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button
                            color={item.is_pos_visible ? menuCategoryToggleMeta.is_pos_visible.enabledColor : "soft-secondary"}
                            size="sm"
                            onClick={() => handleToggleField(item, "is_pos_visible")}
                          >
                            {item.is_pos_visible ? menuCategoryToggleMeta.is_pos_visible.enabledLabel : menuCategoryToggleMeta.is_pos_visible.disabledLabel}
                          </Button>
                        </td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button
                            color={item.is_kiosk_visible ? menuCategoryToggleMeta.is_kiosk_visible.enabledColor : "soft-secondary"}
                            size="sm"
                            onClick={() => handleToggleField(item, "is_kiosk_visible")}
                          >
                            {item.is_kiosk_visible ? menuCategoryToggleMeta.is_kiosk_visible.enabledLabel : menuCategoryToggleMeta.is_kiosk_visible.disabledLabel}
                          </Button>
                        </td>
                        <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                          <Button color="soft-primary" size="sm" className="me-1" onClick={() => navigate(`/table-order/menu-category-form/${item.id}`)}>수정</Button>
                          <Button color="soft-danger" size="sm" onClick={() => handleDelete(item)}>삭제</Button>
                        </td>
                      </tr>))}
                    {items.length === 0 && (<tr>
                        <td colSpan={8} className="text-center text-muted py-8">데이터가 없습니다.</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>)}
          </CardBody>
        </Card>
      </Container>
    </div>);
};
export default MenuCategories;
